import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

interface InactivityPromptProps {
  onLogout: () => void;
  timeout?: number; // milliseconds
  promptBeforeIdle?: number; // milliseconds
}

const InactivityPrompt: React.FC<InactivityPromptProps> = ({
  onLogout,
  timeout = 60 * 60 * 1000, // default 60 minutes
  promptBeforeIdle = 5 * 60 * 1000, // default 5 minutes
}) => {
  const [isPromptOpen, setIsPromptOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);
  const [intervalId, setIntervalId] = useState<NodeJS.Timeout | null>(null);

  // Function to reset the timer
  const resetTimer = useCallback(() => {
    setIsPromptOpen(false);
    setTimeLeft(null);

    if (intervalId) {
      clearInterval(intervalId);
      setIntervalId(null);
    }
  }, [intervalId]);

  // Function to display the prompt
  const displayPrompt = useCallback(() => {
    setIsPromptOpen(true);

    // Calculate remaining time in seconds
    const remainingTime = Math.floor(promptBeforeIdle / 1000);
    setTimeLeft(remainingTime);

    // Set up interval to update the countdown
    const id = setInterval(() => {
      setTimeLeft((prevTimeLeft) => {
        if (prevTimeLeft === null || prevTimeLeft <= 1) {
          clearInterval(id);
          onLogout();
          return 0;
        }
        return prevTimeLeft - 1;
      });
    }, 1000);

    setIntervalId(id);
  }, [promptBeforeIdle, onLogout]);

  // Handle the user staying active
  const handleStayActive = () => {
    resetTimer();
  };

  // Set up activity listeners
  useEffect(() => {
    let idleTimer: NodeJS.Timeout;
    let promptTimer: NodeJS.Timeout;

    const resetTimers = () => {
      clearTimeout(idleTimer);
      clearTimeout(promptTimer);

      // Set timeout for showing prompt
      promptTimer = setTimeout(() => {
        displayPrompt();
      }, timeout - promptBeforeIdle);

      // Set timeout for logout
      idleTimer = setTimeout(() => {
        onLogout();
      }, timeout);
    };

    // Reset timers initially
    resetTimers();

    // Events to track user activity
    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
    ];

    const handleUserActivity = () => {
      if (!isPromptOpen) {
        resetTimers();
      }
    };

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
      clearTimeout(idleTimer);
      clearTimeout(promptTimer);
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [
    timeout,
    promptBeforeIdle,
    onLogout,
    isPromptOpen,
    displayPrompt,
    intervalId,
  ]);

  return (
    <Dialog open={isPromptOpen} onClose={handleStayActive}>
      <DialogTitle>Session Timeout Warning</DialogTitle>
      <DialogContent>
        {timeLeft !== null && (
          <p>
            Your session will expire in {Math.floor(timeLeft / 60)}:
            {(timeLeft % 60).toString().padStart(2, '0')} due to inactivity. Do
            you want to stay signed in?
          </p>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onLogout} color="primary">
          Logout
        </Button>
        <Button onClick={handleStayActive} color="primary" autoFocus>
          Stay Signed In
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InactivityPrompt;
